import { useCallback, useEffect, useRef } from 'react';
import { Keyboard, Platform } from 'react-native';

export const useAutoScrollToBottom = <T extends { scrollToEnd: (opts: { animated: boolean }) => void }>() => {
  const listRef = useRef<T | null>(null);
  const isNearBottomRef = useRef(true);

  const scrollToBottom = useCallback((animated: boolean = true) => {
    listRef.current?.scrollToEnd({ animated });
    isNearBottomRef.current = true;
  }, []);

  const maybeScrollToBottom = useCallback((animated: boolean = false) => {
    if (isNearBottomRef.current) {
      listRef.current?.scrollToEnd({ animated });
    }
  }, []);

  const handleScroll = useCallback((offsetY: number, layoutHeight: number, contentHeight: number, paddingToBottom: number = 24) => {
    const isNearBottom = layoutHeight + offsetY >= contentHeight - paddingToBottom;
    isNearBottomRef.current = isNearBottom;
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const scrollWithDelay = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => scrollToBottom(true), 100);
    };
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showListener = Keyboard.addListener(showEvent, scrollWithDelay);
    const hideListener = Keyboard.addListener(hideEvent, scrollWithDelay);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      showListener.remove();
      hideListener.remove();
    };
  }, [scrollToBottom]);

  return {
    listRef,
    isNearBottomRef,
    scrollToBottom,
    maybeScrollToBottom,
    handleScroll,
  } as const;
};

export default useAutoScrollToBottom;


